import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import AppraisalViewController from "../../redux/actions/AppraisalAction";
import FormDrawer from "../../components/layout/FormDrawer";
import { primaryButtonSx, inputSx } from "../../components/auth/authTheme";

function AppraisalReviewFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const { getAppraisalCycle, addAppraisalReview, updateAppraisalReview } =
    AppraisalViewController();
  const { appraisal, isLoding } = useSelector((state) => state.apprisalcycle);

  const isEdit = Boolean(initialData?.id);

  const [initialValues, setInitialValues] = useState({
    appraisal: "",
    rating: "",
    comments: "",
  });

  useEffect(() => {
    if (!show) return;
    dispatch(getAppraisalCycle());
  }, [show, dispatch]);

  useEffect(() => {
    if (!show) return;

    if (initialData) {
      setInitialValues({
        appraisal: initialData.appraisal || "",
        rating: initialData.rating || "",
        comments: initialData.comments || "",
      });
    } else {
      setInitialValues({
        appraisal: "",
        rating: "",
        comments: "",
      });
    }
  }, [show, initialData?.id]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const action = isEdit
        ? updateAppraisalReview(initialData.id, values)
        : addAppraisalReview(values);
      await dispatch(action);
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting review:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        handleChange,
        isSubmitting,
        submitForm,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleClose}
              width={{ xs: "100%", sm: 480, md: 520 }}
              title={isEdit ? "Edit Appraisal Review" : "Add Appraisal Review"}
              subtitle={
                isEdit
                  ? "Update rating and comments for this review"
                  : "Submit a reviewer rating for an appraisal cycle"
              }
              footer={
                <>
                  <Button onClick={handleClose} color="inherit" disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    onClick={() => submitForm()}
                    sx={{
                      ...primaryButtonSx,
                      minWidth: 120,
                    }}
                    startIcon={
                      isSaving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    {isSaving
                      ? isEdit
                        ? "Updating..."
                        : "Saving..."
                      : isEdit
                        ? "Update"
                        : "Submit"}
                  </Button>
                </>
              }
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={appraisal || []}
                    getOptionLabel={(option) =>
                      `${option.employee_name} — ${option.cycle_start} to ${option.cycle_end}`
                    }
                    value={
                      appraisal?.find((app) => app.id === values.appraisal) ||
                      null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("appraisal", newValue ? newValue.id : "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Appraisal *" sx={inputSx} />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ step: "0.01", min: 0, max: 5 }}
                    label="Rating *"
                    name="rating"
                    value={values.rating}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Comments"
                    name="comments"
                    value={values.comments}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AppraisalReviewFormModal;
